import { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'

import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'

import {
  fetchExperimentVariants,
  fetchProjectScoringConfig,
  ProjectScoringConfig,
  scoreContent,
  ScoreContentResponse,
  ScoreExperiment,
} from './api/api'
import EvaluationProgress from './components/EvaluationProgress'
import PreGeneratedSetup from './components/PreGeneratedSetup'
import RunSummary from './components/RunSummary'
import ScoringConfigSummary from './components/ScoringConfigSummary'
import VariantCard from './components/VariantCard'
import { RuleFinding, ScoreMode, VariantScore } from './types'

const mapResponseToVariantScore = (
  response: ScoreContentResponse,
  text: string,
  experimentName: string
): VariantScore => {
  const {
    scores,
    language_result,
    brand_result,
    settings_result,
    brief_result,
  } = response

  const languageFindings: RuleFinding[] =
    language_result?.results
      .filter((rule) => rule.applicable && !rule.compliant)
      .map((rule) => ({
        label: rule.rule,
        explanation: rule.explanation,
        offendingText: rule.offending_text,
        suggestion: rule.suggestion,
      })) ?? []

  const settingsFindings: RuleFinding[] =
    settings_result?.settings_checks
      .filter((check) => check.applicable && !check.compliant)
      .map((check) => ({
        label: check.field,
        explanation: check.explanation,
        offendingText: check.offending_text,
        suggestion: check.suggestion,
      })) ?? []

  return {
    copy: text,
    experimentName,
    overall: Math.round(scores.average),
    language: language_result
      ? {
          score: language_result.score,
          fluency: language_result.fluency
            ? {
                score: language_result.fluency.score,
                explanation: language_result.fluency.explanation,
              }
            : undefined,
          findings: languageFindings,
        }
      : undefined,
    settings: settings_result
      ? {
          score: settings_result.score,
          applicable: settings_result.applicable,
          passed: settings_result.passed,
          failed: settings_result.failed,
          findings: settingsFindings,
        }
      : undefined,
    brand: brand_result
      ? {
          score: brand_result.overall_score,
          tones: brand_result.tone_results,
        }
      : undefined,
    brief: brief_result
      ? {
          score: brief_result.score,
          explanation: brief_result.explanation,
        }
      : undefined,
  }
}

const Score = () => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  // "Generate language" is temporarily disabled — pre-generated is the only mode.
  const [mode, setMode] = useState<ScoreMode>('pregenerated')

  const [isRunning, setIsRunning] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState<VariantScore[] | null>(null)
  const [scoringModel, setScoringModel] = useState<string>('')
  const [scoringConfig, setScoringConfig] =
    useState<ProjectScoringConfig | null>(null)
  const progressTimers = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearTimers = () => {
    progressTimers.current.forEach(clearTimeout)
    progressTimers.current = []
  }

  const handleReset = useCallback(() => {
    clearTimers()
    setIsRunning(false)
    setIsComplete(false)
    setProgress(0)
    setResults(null)
    setScoringModel('')
    setScoringConfig(null)
  }, [])

  const handleScore = useCallback(
    async (experiments: ScoreExperiment[], projectId: string) => {
      if (isRunning || experiments.length === 0) {
        return
      }
      handleReset()
      setIsRunning(true)
      setProgress(15)

      try {
        const [config, variants] = await Promise.all([
          fetchProjectScoringConfig({ accountId, projectId }),
          fetchExperimentVariants(experiments.map((e) => e.id)),
        ])
        setProgress(45)

        const experimentNameById = new Map(
          experiments.map((e) => [e.id, e.name])
        )

        const variantsToScore = variants.filter((v) => v.text?.trim())

        if (variantsToScore.length === 0) {
          errorToast(
            'No generated language found for the selected experiments.'
          )
          handleReset()
          return
        }

        const rawResponses = await Promise.all(
          variantsToScore.map((variant) =>
            scoreContent({
              content: variant.text,
              campaign_id: variant.campaignId,
              iso_language: config.isoLanguage,
              language_settings: config.languageSettings,
              tones: config.tones,
            })
          )
        )

        const scored = rawResponses.map((response, index) =>
          mapResponseToVariantScore(
            response,
            variantsToScore[index].text,
            experimentNameById.get(variantsToScore[index].campaignId) ?? ''
          )
        )

        setScoringModel(rawResponses[0]?.scoring_model ?? '')
        setScoringConfig(config)
        setProgress(100)
        setIsComplete(true)
        setResults(scored)
      } catch (error) {
        errorToast('Failed to score language for the selected experiments.')
        handleReset()
      }
    },
    [isRunning, handleReset, accountId]
  )

  useEffect(() => {
    return clearTimers
  }, [])

  return (
    <div className="w-full max-w-[960px] mx-auto pt-10 px-6 pb-10 flex flex-col gap-3">
      <div className="flex justify-between items-center mb-2">
        <div>
          <h1 className="text-xl font-bold text-coolGray-800">
            Language quality score
          </h1>
          <p className="text-sm text-coolGray-500 mt-1">
            Load existing language from a project's experiments and score it
            against brand voice configuration and quality dimensions.
          </p>
        </div>
      </div>

      {/* Test setup */}
      <div className="bg-white border border-gold-200 rounded-lg p-4">
        <div className="text-sm font-semibold mb-3">Test setup</div>

        {/* Mode tabs */}
        <div className="flex gap-0.5 bg-gold-50 rounded-md p-1 mb-4">
          <button
            type="button"
            className="flex-1 text-center py-1.5 text-xs font-medium rounded text-coolGray-300 cursor-not-allowed"
            disabled
            title="Coming soon"
          >
            Generate language (coming soon)
          </button>
          <button
            type="button"
            className={cx(
              'flex-1 text-center py-1.5 text-xs font-medium rounded transition-all',
              mode === 'pregenerated'
                ? 'bg-white border border-gold-200 text-coolGray-800'
                : 'text-coolGray-400 hover:text-coolGray-600'
            )}
            onClick={() => setMode('pregenerated')}
          >
            Use pre-generated language
          </button>
        </div>

        <PreGeneratedSetup
          onScore={handleScore}
          onClear={handleReset}
          isScoring={isRunning && !isComplete}
        />
      </div>

      {/* Evaluation results area */}
      {isRunning && (
        <EvaluationProgress
          progress={progress}
          isComplete={isComplete}
          model={scoringModel || 'Scoring…'}
          contentType="Pre-generated language"
          onReset={handleReset}
        />
      )}

      {results && (
        <>
          <RunSummary variants={results} model={scoringModel} />

          {scoringConfig && <ScoringConfigSummary config={scoringConfig} />}

          <div>
            <div className="text-sm font-semibold text-coolGray-500 mb-2">
              Variants
            </div>
            {results.map((variant, i) => (
              <VariantCard key={i} variant={variant} index={i} />
            ))}
          </div>
        </>
      )}

      {!isRunning && !results && (
        <div className="flex flex-col items-center justify-center py-10 text-coolGray-400 text-center">
          <svg
            className="w-7 h-7 mb-2 opacity-35"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"
            />
          </svg>
          <p className="text-xs max-w-[280px]">
            Select a project and one or more experiments to load existing
            language, then score it.
          </p>
        </div>
      )}
    </div>
  )
}

export default Score
